import React from "react";
import { AuthProvider } from "@pankod/refine-core";
import { notification } from "@pankod/refine-antd";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "./firebase";
import bcrypt from "bcryptjs-react";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import { uploadRegistrationFiles } from "./fileUpload";

export const TOKEN_KEY = "auth";
export const USERS = "user";
export const USERS_DETAILS = "user details";

export const authProvider: AuthProvider = {
  login: async ({ email, password }) => {
    try {
      const loginEmail = email.toLowerCase();
      const q = query(
        collection(db, "stores"),
        where("email", "==", loginEmail)
      );
      const querySnapshot: any = await getDocs(q);
      if (querySnapshot?.docs?.length == 0) {
        throw new Error(`No account with email ${email} found.`);
      }
      const userSnapshot = querySnapshot.docs[0];
      let userData = userSnapshot.data();
      const isPasswordCorrect = await bcrypt.compare(
        password,
        userData.password
      );
      if (!isPasswordCorrect) {
        throw new Error("Sorry, incorrect password.");
      }
      return querySnapshot.forEach((doc: any) => {
        let userData = doc.data();
        if (userData.isActive) {
          userData["id"] = doc.id;
          localStorage.setItem(TOKEN_KEY, `${userData.id}`);
          localStorage.setItem(USERS, `${JSON.stringify(userData)}`);
          localStorage.setItem(USERS_DETAILS, `${JSON.stringify(userData)}`);
        } else {
          console.log(userData);
          throw new Error(
            "Your account is currently in the queue for us to review and approve. Please contact us at support@wecarrybags.co.uk if there are any issues."
          );
        }
      });
    } catch (err: any) {
      switch (err.message) {
        case "Firebase: Error (auth/user-not-found).":
          throw new Error(
            "User not found. Please check your email again or sign up."
          );
        case "Firebase: Error (auth/wrong-password).":
          throw new Error(
            "Incorrect password. Please try typing your password again or reset it using the 'Forgot password?' link."
          );
        default:
          console.log(err.message);
          throw new Error(err.message);
      }
    }
  },
  register: async (values) => {
    try {
      // Check if user already exists
      const q = query(
        collection(db, "stores"),
        where("email", "==", values.email)
      );
      const querySnapshot: any = await getDocs(q);
      if (querySnapshot.docs.length > 0) {
        throw new Error("Sorry. User with this email already exists.");
      }

      // Show initial processing message (centered)
      notification.info({
        message: "Processing Registration",
        description: "Please wait while we upload your files and process your data. This may take a moment...",
        duration: 0, // Don't auto-close
        key: "registration-progress",
        placement: "top",
        style: {
          marginTop: "40vh",
        },
        className: "centered-notification",
      });

      // Upload all files to Firebase Storage before saving to Firestore
      let uploadedFiles;
      try {
        notification.info({
          message: "Uploading Files",
          description: "Uploading store logo, images, and product photos...",
          duration: 0,
          key: "registration-progress",
          placement: "top",
          style: {
            marginTop: "40vh",
          },
          className: "centered-notification",
        });

        uploadedFiles = await uploadRegistrationFiles({
          avatar: values.avatar,
          images: values.images,
          products: values.products,
        });
      } catch (uploadError: any) {
        // Close the progress notification
        notification.destroy("registration-progress");
        throw new Error(`File upload failed: ${uploadError.message}`);
      }

      // VERSION CHECK: Data format fixes applied (Dec 23, 2025)
      // Convert operational hours from array format to object format expected by VenderTimes component
      // From: [{ day: "Monday", open: "09:00", close: "17:00", closed: false }, ...]
      // To: { Mon: [{ open_at: "09:00", close_at: "17:00" }], Tue: [...], ... }
      const dayMapping: Record<string, string> = {
        "Monday": "Mon",
        "Tuesday": "Tue",
        "Wednesday": "Wed",
        "Thursday": "Thu",
        "Friday": "Fri",
        "Saturday": "Sat",
        "Sunday": "Sun"
      };

      const operationalHoursFormatted: any = {};
      if (values.operationalHours && Array.isArray(values.operationalHours)) {
        values.operationalHours.forEach((dayHours: any) => {
          const shortDay = dayMapping[dayHours.day];
          if (shortDay) {
            operationalHoursFormatted[shortDay] = [{
              open_at: dayHours.open || "00:00",
              close_at: dayHours.close || "00:00",
              is_close: dayHours.closed || false
            }];
          }
        });
      }

      // Convert avatar and images to the format expected by Files component
      // Files component expects: [{ url: string, name: string, uid: string, status: string, ... }]
      const avatarFormatted = uploadedFiles.avatarUrl ? [{
        url: uploadedFiles.avatarUrl,
        name: values.avatar?.name || "avatar",
        uid: `avatar-${Date.now()}`,
        status: "done",
        type: values.avatar?.type || "image/jpeg",
        size: values.avatar?.size || 0,
        percent: 100
      }] : [];

      const imagesFormatted = uploadedFiles.imageUrls.map((url: string, index: number) => ({
        url: url,
        name: values.images[index]?.name || `image-${index}`,
        uid: `image-${Date.now()}-${index}`,
        status: "done",
        type: values.images[index]?.type || "image/jpeg",
        size: values.images[index]?.size || 0,
        percent: 100
      }));

      // Convert product images to the format expected by PImg component
      // PImg expects: { description: string, image: [{ url, name, uid, status, ... }] }
      const productsFormatted = uploadedFiles.productUrls.map((p: any, index: number) => ({
        description: p.description,
        image: p.imageUrl ? [{
          url: p.imageUrl,
          name: values.products[index]?.image?.name || `product-${index}`,
          uid: `product-${Date.now()}-${index}`,
          status: "done",
          type: values.products[index]?.image?.type || "image/jpeg",
          size: values.products[index]?.image?.size || 0,
          percent: 100
        }] : []
      }));

      // Prepare data with uploaded file URLs instead of File objects
      const dataToSave = {
        ...values,
        avatar: avatarFormatted, // Store as array of file objects for Files component
        images: imagesFormatted, // Store as array of file objects for Files component
        operationalHours: operationalHoursFormatted, // Store in VenderTimes expected format
        products: productsFormatted, // Store as array with image as file object for PImg component
      };

      // Send confirmation email with contract
      try {
        const managementEmail = values.email;
        const representativeEmail = values.representativeEmail;
        const retailerName = values.title;
        const retailerLegalName = values.legalTitle;
        const representativeName = values.representativeName;
        const { country, city, address2, postal_code } = values.address;

        // Safely parse address - handle cases with no commas, one comma, or multiple commas
        let address = address2 as string;
        if (address2 && typeof address2 === 'string' && address2.includes(',')) {
          const addressParts = address2.split(",").map(part => part.trim());
          // Take first two parts if available, otherwise use full address
          address = addressParts.length >= 2
            ? `${addressParts[0]}, ${addressParts[1]}`
            : addressParts[0] || address2;
        }

        const params = {
          managementEmail,
          representativeEmail,
          retailerName,
          retailerLegalName,
          representativeName,
          country,
          city,
          postalCode: postal_code,
          address,
        };

        notification.info({
          message: "Sending Contract",
          description: "Emailing your collaboration contract...",
          duration: 0,
          key: "registration-progress",
          placement: "top",
          style: {
            marginTop: "40vh",
          },
          className: "centered-notification",
        });

        await axios.post(
          "https://backend.wecarrybags.co.uk/api/v1/partner/email-contract",
          params,
          { headers: { Authorization: `Bearer ${process.env.REACT_APP_INTERNAL_OPS_SECRET}` } }
        );
      } catch (err) {
        console.log("Email contract error (non-blocking):", err);
        // Don't fail registration if email fails - it's a non-critical feature
      }

      // Hash password and save to Firestore
      notification.info({
        message: "Finalizing Account",
        description: "Creating your account...",
        duration: 0,
        key: "registration-progress",
        placement: "top",
        style: {
          marginTop: "40vh",
        },
        className: "centered-notification",
      });

      const salt = await bcrypt.genSalt(9);
      const hashedPassword = await bcrypt.hash(values.password, salt);
      const dbRef = collection(db, "stores");
      const id = uuidv4();
      const normalisedEmail = values.email.toLowerCase();

      await addDoc(dbRef, {
        ...dataToSave, // Use data with URLs instead of File objects
        email: normalisedEmail,
        uid: id,
        id: id,
        type: "vendor",
        isActive: false,
        createAt: String(new Date()),
        updateAt: String(new Date()),
        password: hashedPassword,
      });

      // Close progress notification
      notification.destroy("registration-progress");

      // Show success with clear next steps (top right)
      notification.success({
        message: "Account Created Successfully!",
        description: React.createElement(
          "div",
          null,
          React.createElement("div", { style: { marginBottom: "0.5rem" } }, "Your account has been created and a collaboration contract has been sent to your email. Your account is pending approval."),
          React.createElement("div", null, "We'll review your application and notify you within 24-48 hours. You cannot login until your account is approved.")
        ),
        duration: 15, // Show for 15 seconds so users can read it
      });
    } catch (error: any) {
      // Close any progress notifications
      notification.destroy("registration-progress");

      notification.error({
        message: "Registration Failed",
        description: error.message || "An unexpected error occurred. Please try again.",
        duration: 10,
      });
      return Promise.reject(error);
    }
  },
  updatePassword: async () => {
    // Firebase doesn't allow updating password.
    notification.success({
      message: "Updated Password",
      description: "Password updated successfully",
    });
    return Promise.resolve();
  },
  forgotPassword: async ({ email }) => {
    try {
      const loginEmail = email.toLowerCase();
      const q = query(
        collection(db, "stores"),
        where("email", "==", loginEmail)
      );
      const querySnapshot: any = await getDocs(q);
      if (querySnapshot?.docs?.length == 0) {
        throw new Error(`No account with email ${email} found.`);
      }
      const userSnapshot = querySnapshot.docs[0];
      const userData = userSnapshot.data();
      const body = {
        email: loginEmail,
        id: userData.id,
      };
      await axios.post(
        "https://backend.wecarrybags.co.uk/api/v1/partner/get-password-reset-link",
        body,
        { headers: { Authorization: `Bearer ${process.env.REACT_APP_INTERNAL_OPS_SECRET}` } }
      );
      notification.success({
        message: "Reset Password",
        description: `Reset password link sent to "${email}"`,
      });
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  },
  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USERS);
    localStorage.removeItem(USERS_DETAILS);
    return Promise.resolve();
  },
  checkError: () => Promise.resolve(),
  checkAuth: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      return Promise.resolve();
    }
    return Promise.reject();
  },
  getPermissions: () => Promise.resolve(),
  getUserIdentity: async () => {
    const user = localStorage.getItem(USERS);
    if (!user) {
      return Promise.reject();
    }
    let users = JSON.parse(user);
    const q = query(collection(db, "stores"), where("uid", "==", users.uid));
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      let userData: any = { ...doc.data(), id: doc.id };
      localStorage.setItem(USERS_DETAILS, `${JSON.stringify(userData)}`);
      return Promise.resolve({
        id: userData.id,
        name: userData.title,
        avatar: userData.avatar,
      });
    });
  },
};

type resetPasswordParams = {
  email: string;
  password: string;
  id: string;
};

export const passwordProvider = {
  resetPassword: async ({ email, password, id }: resetPasswordParams) => {
    const q = query(collection(db, "stores"), where("email", "==", email));
    const querySnapshot = await getDocs(q);
    if (querySnapshot?.docs?.length == 0) {
      throw new Error(`No account with email ${email} found.`);
    }
    const userSnapshot = querySnapshot.docs[0];
    const userData = userSnapshot.data();
    if (userData.id !== id) {
      throw new Error("Invalid link.");
    }

    const salt = await bcrypt.genSalt(9);
    const hashedPassword = await bcrypt.hash(password, salt);
    const userRef = doc(db, "stores", userSnapshot.id);
    await updateDoc(userRef, {
      password: hashedPassword,
    });
  },
};
